import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./ImageComputer.css";
import { useActionExecutor } from "./ActionExecutor.js";
import Cropper from "react-easy-crop";

export default function ImageComputer({ isOpen = false, onClose }) {
  const [imageFile, setImageFile] = useState(null);
  const [batchMode, setBatchMode] = useState(false);
  const [batchFiles, setBatchFiles] = useState([]); // File[]
  const [batchProgress, setBatchProgress] = useState(null); // { done, total } | null
  const [maskFile, setMaskFile] = useState(null);
  const [overlayFile, setOverlayFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [outputUrl, setOutputUrl] = useState(null);
  const [inputMeta, setInputMeta] = useState(null); // { width, height, aspect }
  const [toast, setToast] = useState(null);
  const [approvalMode, setApprovalMode] = useState(true);
  const [allowedTypes, setAllowedTypes] = useState(["uploadImage", "process", "aiEdit", "download"]);
  const [verification, setVerification] = useState(null);
  const [verificationFix, setVerificationFix] = useState(null);
  const [versions, setVersions] = useState([]); // [{ id, name, url, recipe, verification }]
  const [compareId, setCompareId] = useState(null);
  const projectFileInputRef = useRef(null);
  const [actionSteps, setActionSteps] = useState([]); // [{ ts, label, state }]
  const [actionIndex, setActionIndex] = useState(-1);

  const [mode, setMode] = useState("local"); // local | ai
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiInpaint, setAiInpaint] = useState(false);
  const [aiGoal, setAiGoal] = useState("");
  const [aiPlanContract, setAiPlanContract] = useState(null);
  const [aiPendingRecipe, setAiPendingRecipe] = useState(null);

  // Local recipe controls
  const [preset, setPreset] = useState("none"); // none | sepia | cinematic
  const [format, setFormat] = useState("png");
  const [resizeW, setResizeW] = useState("");
  const [resizeH, setResizeH] = useState("");
  const [cropEnabled, setCropEnabled] = useState(false);
  const [cropLeft, setCropLeft] = useState("0");
  const [cropTop, setCropTop] = useState("0");
  const [cropWidth, setCropWidth] = useState("512");
  const [cropHeight, setCropHeight] = useState("512");
  const [interactiveCrop, setInteractiveCrop] = useState(true);
  const [cropPos, setCropPos] = useState({ x: 0, y: 0 });
  const [cropZoom, setCropZoom] = useState(1);
  const [cropAspect, setCropAspect] = useState(1 / 1);
  const [cropGuide, setCropGuide] = useState("thirds"); // none | thirds | golden | center
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [rotate, setRotate] = useState("0");
  const [flip, setFlip] = useState(false);
  const [flop, setFlop] = useState(false);
  const [grayscale, setGrayscale] = useState(false);
  const [blur, setBlur] = useState("0");
  const [sharpen, setSharpen] = useState(false);
  const [brightness, setBrightness] = useState("1");
  const [saturation, setSaturation] = useState("1");
  const [text, setText] = useState("");
  const [textX, setTextX] = useState("20");
  const [textY, setTextY] = useState("60");
  const [textSize, setTextSize] = useState("36");
  const [textColor, setTextColor] = useState("white");
  const [layerOrder, setLayerOrder] = useState(["text", "image"]); // top-to-bottom order for overlays
  const [layerVisible, setLayerVisible] = useState({ text: true, image: true });
  const [vignette, setVignette] = useState(false);
  const [vignetteStrength, setVignetteStrength] = useState("0.55");
  const [removeBg, setRemoveBg] = useState(false);
  const [removeBgInvert, setRemoveBgInvert] = useState(false);
  const [removeBgFeather, setRemoveBgFeather] = useState("0");
  const [rounded, setRounded] = useState("0");
  const [borderSize, setBorderSize] = useState("0");
  const [borderColor, setBorderColor] = useState("#ffffff");
  const [shadow, setShadow] = useState(false);
  const [shadowOffset, setShadowOffset] = useState("12");
  const [shadowBlur, setShadowBlur] = useState("18");
  const [shadowAlpha, setShadowAlpha] = useState("0.35");

  // Resizable
  const DEFAULT_SIZE = { width: 980, height: 760 };
  const MIN_SIZE = { width: 640, height: 520 };
  const MAX_SIZE = { width: 1400, height: 980 };
  const [panelSize, setPanelSize] = useState(() => {
    try {
      const raw = localStorage.getItem("zolaid-image-computer-size");
      if (!raw) return DEFAULT_SIZE;
      const p = JSON.parse(raw);
      const w = Number(p?.width);
      const h = Number(p?.height);
      if (!Number.isFinite(w) || !Number.isFinite(h)) return DEFAULT_SIZE;
      return {
        width: Math.max(MIN_SIZE.width, Math.min(MAX_SIZE.width, w)),
        height: Math.max(MIN_SIZE.height, Math.min(MAX_SIZE.height, h)),
      };
    } catch (_) {
      return DEFAULT_SIZE;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("zolaid-image-computer-size", JSON.stringify(panelSize));
    } catch (_) {}
  }, [panelSize]);

  useEffect(() => {
    if (!imageUrl) {
      setInputMeta(null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || 0;
      const h = img.naturalHeight || 0;
      setInputMeta(w && h ? { width: w, height: h, aspect: w / h } : null);
    };
    img.onerror = () => setInputMeta(null);
    img.src = imageUrl;
  }, [imageUrl]);

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = panelSize.width;
    const startH = panelSize.height;
    const onMove = (ev) => {
      const nextW = Math.max(MIN_SIZE.width, Math.min(MAX_SIZE.width, startW + (ev.clientX - startX)));
      const nextH = Math.max(MIN_SIZE.height, Math.min(MAX_SIZE.height, startH + (ev.clientY - startY)));
      setPanelSize({ width: nextW, height: nextH });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const applyToast = (msg) => {
    setToast(msg);
    window.clearTimeout(applyToast.__t);
    applyToast.__t = window.setTimeout(() => setToast(null), 1600);
  };

  const saveToImageLibrary = (url) => {
    if (!url) return;
    try {
      const key = "zolaid-image-library";
      const raw = localStorage.getItem(key);
      const prev = raw ? JSON.parse(raw) : [];
      const arr = Array.isArray(prev) ? prev : [];
      const next = [{ url, ts: Date.now() }, ...arr.filter((x) => x && x.url !== url)].slice(0, 30);
      localStorage.setItem(key, JSON.stringify(next));
    } catch (_) {}
  };

  const snapToGuides = (value, guides, tolerance = 10) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return value;
    let best = n;
    let bestDist = tolerance + 1;
    for (const g of guides) {
      const d = Math.abs(n - g);
      if (d < bestDist) {
        bestDist = d;
        best = g;
      }
    }
    return bestDist <= tolerance ? String(Math.round(best)) : String(Math.round(n));
  };

  const recipe = useMemo(() => {
    const textOverlays = text.trim()
      ? [
          {
            text,
            x: Number(textX) || 20,
            y: Number(textY) || 60,
            size: Number(textSize) || 36,
            color: textColor || "white",
            box: true,
            boxColor: "rgba(0,0,0,0.45)",
            padding: 12,
          },
        ]
      : [];

    const layers = [];
    for (const key of layerOrder) {
      if (key === "text") {
        if (textOverlays.length) layers.push({ type: "text", visible: layerVisible.text !== false, ...textOverlays[0] });
      } else if (key === "image") {
        if (overlayFile) layers.push({ type: "image", visible: layerVisible.image !== false, x: 20, y: 20, width: 240, opacity: 0.85 });
      }
    }
    return {
      preset,
      format,
      resize: {
        width: resizeW === "" ? null : Number(resizeW),
        height: resizeH === "" ? null : Number(resizeH),
        fit: "cover",
      },
      crop: cropEnabled
        ? {
            left: Number(cropLeft) || 0,
            top: Number(cropTop) || 0,
            width: Number(cropWidth) || 1,
            height: Number(cropHeight) || 1,
          }
        : null,
      rotate: Number(rotate) || 0,
      flip,
      flop,
      grayscale,
      blur: Number(blur) || 0,
      sharpen,
      vignette,
      vignetteStrength: Number(vignetteStrength) || 0.55,
      backgroundRemoval: {
        enabled: removeBg,
        mode: "mask",
        invert: removeBgInvert,
        feather: Number(removeBgFeather) || 0,
      },
      rounded: Number(rounded) || 0,
      border: { size: Number(borderSize) || 0, color: borderColor || "#ffffff" },
      shadow,
      shadowOffset: Number(shadowOffset) || 12,
      shadowBlur: Number(shadowBlur) || 18,
      shadowAlpha: Number(shadowAlpha) || 0.35,
      modulate: {
        brightness: Number(brightness) || 1,
        saturation: Number(saturation) || 1,
        hue: 0,
      },
      textOverlays,
      imageOverlay: overlayFile ? { x: 20, y: 20, width: 240, opacity: 0.85 } : null,
      layers,
    };
  }, [
    preset,
    format,
    resizeW,
    resizeH,
    cropEnabled,
    cropLeft,
    cropTop,
    cropWidth,
    cropHeight,
    rotate,
    flip,
    flop,
    grayscale,
    blur,
    sharpen,
    vignette,
    vignetteStrength,
    removeBg,
    removeBgInvert,
    removeBgFeather,
    rounded,
    borderSize,
    borderColor,
    shadow,
    shadowOffset,
    shadowBlur,
    shadowAlpha,
    brightness,
    saturation,
    text,
    textX,
    textY,
    textSize,
    textColor,
    overlayFile,
    layerOrder,
    layerVisible,
  ]);

  const actions = useMemo(() => {
    const list = [];
    if (imageFile) list.push({ type: "uploadImage", description: "Image uploaded", delay: 100 });
    if (mode === "local" && imageFile) list.push({ type: "process", description: "Apply local edits (Sharp)", delay: 100 });
    if (mode === "ai" && imageFile && (aiPrompt.trim() || aiInpaint)) list.push({ type: "aiEdit", description: aiInpaint ? "Inpaint (masked)" : "AI edit (DALL-E)", delay: 100 });
    if (outputUrl) list.push({ type: "download", description: "Download output", delay: 100 });
    return list;
  }, [imageFile, mode, aiPrompt, aiInpaint, outputUrl]);

  const doLocalProcess = async () => {
    const files = batchMode && batchFiles.length ? batchFiles : imageFile ? [imageFile] : [];
    if (!files.length) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/image/process` : "/image/process";

      if (files.length > 1) {
        setBatchProgress({ done: 0, total: files.length });
        applyToast(`Batch processing (0/${files.length})â€¦`);
      } else {
        setBatchProgress(null);
        applyToast("Processingâ€¦");
      }

      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const formData = new FormData();
        formData.append("image", f);
        if (overlayFile) formData.append("overlay", overlayFile);
        if (removeBg && maskFile) formData.append("mask", maskFile);
        formData.append("recipe", JSON.stringify(recipe));
        const res = await axios.post(endpoint, formData, { headers: { "Content-Type": "multipart/form-data" } });
        if (!res.data?.imageUrl) continue;

        setOutputUrl(res.data.imageUrl);
        saveToImageLibrary(res.data.imageUrl);
        setVerification(res.data.verification || null);
        setVerificationFix(res.data.verificationFix || null);

        setVersions((prev) => [
          { id: `${Date.now()}-${i}`, name: files.length > 1 ? `Batch ${f.name}` : `Local ${prev.length + 1}`, url: res.data.imageUrl, recipe, verification: res.data.verification },
          ...prev,
        ].slice(0, 25));

        if (res.data?.fallbackBranch?.imageUrl) {
          const fb = res.data.fallbackBranch;
          const id = `fallback-${Date.now()}-${i}`;
          const name = "Fallback (safe)";
          setVersions((prev) => [{ id, name, url: fb.imageUrl, recipe: { safe: true }, verification: { ok: true } }, ...prev].slice(0, 25));
          setCompareId(id);
        }

        if (files.length > 1) {
          setBatchProgress({ done: i + 1, total: files.length });
          applyToast(`Batch processing (${i + 1}/${files.length})â€¦`);
        }
      }

      addCheckpoint(files.length > 1 ? "Batch applied" : "Local applied");
      setBatchProgress(null);
      applyToast("Done");
    } catch (e) {
      console.error(e);
      applyToast("Process failed");
      setBatchProgress(null);
    }
  };

  const doAiEdit = async () => {
    if (!imageFile) return;
    if (aiInpaint && !maskFile) {
      applyToast("Upload a mask first for inpainting");
      return;
    }
    const finalPrompt = aiPrompt.trim() || (aiInpaint ? "Inpaint the masked/transparent area naturally, matching the surrounding image." : "");
    if (!finalPrompt) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/chat/edit-image` : "/chat/edit-image";
      const verifyEndpoint = apiUrl ? `${apiUrl}/image/verify` : "/image/verify";
      const expectations = inputMeta?.aspect ? { aspect: inputMeta.aspect, aspectTolerance: 0.02 } : {};

      const tryOnce = async (promptText) => {
        const formData = new FormData();
        formData.append("image", imageFile);
        if (maskFile) formData.append("mask", maskFile);
        formData.append("prompt", promptText);
        const res = await axios.post(endpoint, formData, { headers: { "Content-Type": "multipart/form-data" } });
        const url = res.data?.imageUrl || null;
        if (!url) return { ok: false, error: res.data?.error || "AI edit failed", imageUrl: null, verification: null };
        const v = await axios.post(verifyEndpoint, { imageUrl: url, expectations }).then((r) => r.data?.verification || null).catch(() => null);
        return { ok: Boolean(v?.ok), imageUrl: url, verification: v };
      };

      applyToast(aiInpaint ? "AI inpaintingâ€¦" : "AI editingâ€¦");
      const first = await tryOnce(finalPrompt);
      if (!first.imageUrl) {
        applyToast(first.error || "AI edit failed");
        return;
      }

      // Level 5: auto-replan/retry if verification failed
      let final = first;
      if (!first.ok) {
        applyToast("Verification failed â€” retrying saferâ€¦");
        const safer = `${finalPrompt}\n\nConstraints: keep the exact original aspect ratio and do NOT add borders. Preserve overall framing.`;
        final = await tryOnce(safer);
      }

      setOutputUrl(final.imageUrl);
      saveToImageLibrary(final.imageUrl);
      setVerification(final.verification || { ok: true, checks: [{ name: "has_url", ok: true }] });
      setVersions((prev) => [{ id: Date.now().toString(), name: aiInpaint ? `Inpaint ${prev.length + 1}` : `AI ${prev.length + 1}`, url: final.imageUrl, recipe: { aiPrompt: finalPrompt, aiInpaint }, verification: final.verification || { ok: true } }, ...prev].slice(0, 25));
      applyToast(final.ok ? "AI done" : "AI done (but checks failed)");
    } catch (e) {
      console.error(e);
      applyToast("AI edit failed");
    }
  };

  const onActionComplete = (a) => {
    if (a.type === "process") doLocalProcess();
    if (a.type === "aiEdit") doAiEdit();
    if (a.type === "download" && outputUrl) {
      const link = document.createElement("a");
      link.href = outputUrl;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      link.remove();
      applyToast("Download started");
    }
  };

  const { currentAction, executedActions, awaitingApproval, approveNext, skipNext, reset } = useActionExecutor(actions, onActionComplete, {
    startIndex: 0,
    requireApproval: approvalMode,
    allowedActionTypes: allowedTypes,
  });

  const compare = versions.find((v) => v.id === compareId) || null;

  const downloadTextFile = (filename, content, mime = "text/plain;charset=utf-8") => {
    const blob = new Blob([content || ""], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const saveProjectJson = () => {
    downloadTextFile("zolaid-image-project.json", JSON.stringify({ mode, aiPrompt, recipe }, null, 2), "application/json;charset=utf-8");
  };

  const loadProjectJson = async (file) => {
    try {
      if (!file) return;
      const t = await file.text();
      const p = JSON.parse(t);
      if (!p || typeof p !== "object") throw new Error("Invalid JSON");
      if (typeof p.mode === "string") setMode(p.mode);
      if (typeof p.aiPrompt === "string") setAiPrompt(p.aiPrompt);
      const rr = p.recipe && typeof p.recipe === "object" ? p.recipe : null;
      if (rr) {
        if (typeof rr.preset === "string") setPreset(rr.preset);
        if (typeof rr.format === "string") setFormat(rr.format);
        if (rr.resize && typeof rr.resize === "object") {
          if (rr.resize.width != null) setResizeW(String(rr.resize.width));
          if (rr.resize.height != null) setResizeH(String(rr.resize.height));
        }
        if (rr.crop && typeof rr.crop === "object") {
          setCropEnabled(true);
          if (rr.crop.left != null) setCropLeft(String(rr.crop.left));
          if (rr.crop.top != null) setCropTop(String(rr.crop.top));
          if (rr.crop.width != null) setCropWidth(String(rr.crop.width));
          if (rr.crop.height != null) setCropHeight(String(rr.crop.height));
        }
        if (rr.rotate != null) setRotate(String(rr.rotate));
        setGrayscale(Boolean(rr.grayscale));
        if (rr.blur != null) setBlur(String(rr.blur));
        setSharpen(Boolean(rr.sharpen));
        if (rr.modulate && typeof rr.modulate === "object") {
          if (rr.modulate.brightness != null) setBrightness(String(rr.modulate.brightness));
          if (rr.modulate.saturation != null) setSaturation(String(rr.modulate.saturation));
        }
        setVignette(Boolean(rr.vignette));
        if (rr.vignetteStrength != null) setVignetteStrength(String(rr.vignetteStrength));
        if (rr.rounded != null) setRounded(String(rr.rounded));
        if (rr.border && typeof rr.border === "object") {
          if (rr.border.size != null) setBorderSize(String(rr.border.size));
          if (typeof rr.border.color === "string") setBorderColor(rr.border.color);
        }
        // text overlay (single in UI)
        const to = Array.isArray(rr.textOverlays) ? rr.textOverlays[0] : null;
        if (to) {
          setText(String(to.text || ""));
          if (to.x != null) setTextX(String(to.x));
          if (to.y != null) setTextY(String(to.y));
          if (to.size != null) setTextSize(String(to.size));
          if (to.color) setTextColor(String(to.color));
        }
      }
      applyToast("Project loaded");
    } catch (e) {
      console.error(e);
      applyToast("Load failed");
    } finally {
      if (projectFileInputRef.current) projectFileInputRef.current.value = "";
    }
  };

  const applyRecipeToUi = (rr) => {
    if (!rr || typeof rr !== "object") return;
    if (typeof rr.preset === "string") setPreset(rr.preset);
    if (typeof rr.format === "string") setFormat(rr.format);
    if (rr.resize && typeof rr.resize === "object") {
      setResizeW(rr.resize.width == null ? "" : String(rr.resize.width));
      setResizeH(rr.resize.height == null ? "" : String(rr.resize.height));
    }
    if (rr.crop && typeof rr.crop === "object") {
      setCropEnabled(true);
      setCropLeft(rr.crop.left == null ? "0" : String(rr.crop.left));
      setCropTop(rr.crop.top == null ? "0" : String(rr.crop.top));
      setCropWidth(rr.crop.width == null ? "512" : String(rr.crop.width));
      setCropHeight(rr.crop.height == null ? "512" : String(rr.crop.height));
    }
    if (rr.rotate != null) setRotate(String(rr.rotate));
    if (typeof rr.flip === "boolean") setFlip(rr.flip);
    if (typeof rr.flop === "boolean") setFlop(rr.flop);
    setGrayscale(Boolean(rr.grayscale));
    if (rr.blur != null) setBlur(String(rr.blur));
    setSharpen(Boolean(rr.sharpen));
    if (rr.modulate && typeof rr.modulate === "object") {
      setBrightness(rr.modulate.brightness == null ? "1" : String(rr.modulate.brightness));
      setSaturation(rr.modulate.saturation == null ? "1" : String(rr.modulate.saturation));
    }
    setVignette(Boolean(rr.vignette));
    if (rr.vignetteStrength != null) setVignetteStrength(String(rr.vignetteStrength));
    if (rr.backgroundRemoval && typeof rr.backgroundRemoval === "object") {
      setRemoveBg(Boolean(rr.backgroundRemoval.enabled));
      setRemoveBgInvert(Boolean(rr.backgroundRemoval.invert));
      if (rr.backgroundRemoval.feather != null) setRemoveBgFeather(String(rr.backgroundRemoval.feather));
    }
    if (rr.rounded != null) setRounded(String(rr.rounded));
    if (rr.border && typeof rr.border === "object") {
      setBorderSize(rr.border.size == null ? "0" : String(rr.border.size));
      if (typeof rr.border.color === "string") setBorderColor(rr.border.color);
    }
    setShadow(Boolean(rr.shadow));
    if (rr.shadowOffset != null) setShadowOffset(String(rr.shadowOffset));
    if (rr.shadowBlur != null) setShadowBlur(String(rr.shadowBlur));
    if (rr.shadowAlpha != null) setShadowAlpha(String(rr.shadowAlpha));

    const to = Array.isArray(rr.textOverlays) ? rr.textOverlays[0] : null;
    if (to) {
      setText(String(to.text || ""));
      setTextX(to.x == null ? "20" : String(to.x));
      setTextY(to.y == null ? "60" : String(to.y));
      setTextSize(to.size == null ? "36" : String(to.size));
      if (to.color) setTextColor(String(to.color));
    }
  };

  const captureUiState = () => ({
    preset,
    format,
    resizeW,
    resizeH,
    cropEnabled,
    cropLeft,
    cropTop,
    cropWidth,
    cropHeight,
    interactiveCrop,
    cropZoom,
    cropAspect,
    cropGuide,
    rotate,
    flip,
    flop,
    grayscale,
    blur,
    sharpen,
    brightness,
    saturation,
    vignette,
    vignetteStrength,
    rounded,
    borderSize,
    borderColor,
    shadow,
    shadowOffset,
    shadowBlur,
    shadowAlpha,
    text,
    textX,
    textY,
    textSize,
    textColor,
    layerOrder,
    layerVisible,
  });

  const applyUiState = (s) => {
    if (!s || typeof s !== "object") return;
    if (s.preset != null) setPreset(s.preset);
    if (s.format != null) setFormat(s.format);
    if (s.resizeW != null) setResizeW(s.resizeW);
    if (s.resizeH != null) setResizeH(s.resizeH);
    if (s.cropEnabled != null) setCropEnabled(Boolean(s.cropEnabled));
    if (s.cropLeft != null) setCropLeft(s.cropLeft);
    if (s.cropTop != null) setCropTop(s.cropTop);
    if (s.cropWidth != null) setCropWidth(s.cropWidth);
    if (s.cropHeight != null) setCropHeight(s.cropHeight);
    if (s.interactiveCrop != null) setInteractiveCrop(Boolean(s.interactiveCrop));
    if (s.cropZoom != null) setCropZoom(Number(s.cropZoom) || 1);
    if (s.cropAspect != null) setCropAspect(Number(s.cropAspect) || 1);
    if (s.cropGuide != null) setCropGuide(s.cropGuide);
    if (s.rotate != null) setRotate(s.rotate);
    if (s.flip != null) setFlip(Boolean(s.flip));
    if (s.flop != null) setFlop(Boolean(s.flop));
    if (s.grayscale != null) setGrayscale(Boolean(s.grayscale));
    if (s.blur != null) setBlur(s.blur);
    if (s.sharpen != null) setSharpen(Boolean(s.sharpen));
    if (s.brightness != null) setBrightness(s.brightness);
    if (s.saturation != null) setSaturation(s.saturation);
    if (s.vignette != null) setVignette(Boolean(s.vignette));
    if (s.vignetteStrength != null) setVignetteStrength(s.vignetteStrength);
    if (s.rounded != null) setRounded(s.rounded);
    if (s.borderSize != null) setBorderSize(s.borderSize);
    if (s.borderColor != null) setBorderColor(s.borderColor);
    if (s.shadow != null) setShadow(Boolean(s.shadow));
    if (s.shadowOffset != null) setShadowOffset(s.shadowOffset);
    if (s.shadowBlur != null) setShadowBlur(s.shadowBlur);
    if (s.shadowAlpha != null) setShadowAlpha(s.shadowAlpha);
    if (s.text != null) setText(s.text);
    if (s.textX != null) setTextX(s.textX);
    if (s.textY != null) setTextY(s.textY);
    if (s.textSize != null) setTextSize(s.textSize);
    if (s.textColor != null) setTextColor(s.textColor);
    if (Array.isArray(s.layerOrder)) setLayerOrder(s.layerOrder);
    if (s.layerVisible && typeof s.layerVisible === "object") setLayerVisible(s.layerVisible);
  };

  const addCheckpoint = (label) => {
    const step = { ts: Date.now(), label: label || "Checkpoint", state: captureUiState() };
    setActionSteps((prev) => [step, ...prev].slice(0, 40));
    setActionIndex(0);
  };

  const undo = () => {
    const nextIdx = actionIndex + 1;
    const step = actionSteps[nextIdx];
    if (!step) return;
    setActionIndex(nextIdx);
    applyUiState(step.state);
    applyToast(`Undo: ${step.label}`);
  };

  const redo = () => {
    const nextIdx = actionIndex - 1;
    if (nextIdx < 0) return;
    const step = actionSteps[nextIdx];
    if (!step) return;
    setActionIndex(nextIdx);
    applyUiState(step.state);
    applyToast(`Redo: ${step.label}`);
  };

  const requestAiPlan = async () => {
    if (!aiGoal.trim()) return;
    try {
      applyToast("Asking AIâ€¦");
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/chat` : "/chat";
      const msg = [
        "Create an image edit plan and recipe.",
        "Return mode=plan.",
        "Put the Sharp recipe in artifacts.imageRecipe.",
        "Also include artifacts.plan and artifacts.expected checks (like: size/aspect/background removal).",
        "",
        `Goal: ${aiGoal}`,
        `Current file: ${imageFile ? imageFile.name : "none"}`,
      ].join("\n");
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: "image-computer", message: msg }),
      });
      const data = await res.json();
      const contract = data.contract || null;
      setAiPlanContract(contract);
      const recipe = contract?.artifacts?.imageRecipe || null;
      if (recipe && typeof recipe === "object") {
        setAiPendingRecipe(recipe);
        applyToast("AI plan ready");
      } else {
        applyToast("AI returned no recipe");
      }
    } catch (e) {
      console.error(e);
      applyToast("AI plan failed");
    }
  };

  const exportOutput = async (fmt) => {
    if (!outputUrl) return;
    try {
      applyToast("Exportingâ€¦");
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/image/export` : "/image/export";
      const res = await axios.post(endpoint, { imageUrl: outputUrl, format: fmt, quality: 92 });
      if (res.data?.imageUrl) {
        const a = document.createElement("a");
        a.href = res.data.imageUrl;
        a.download = "";
        document.body.appendChild(a);
        a.click();
        a.remove();
        applyToast("Export ready");
      } else {
        applyToast("Export failed");
      }
    } catch (e) {
      console.error(e);
      applyToast("Export failed");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="image-computer-modal">
      <div className="image-computer" style={{ width: panelSize.width, height: panelSize.height }}>
        {toast && <div className="image-toast">{toast}</div>}
        <div className="image-resize-corner" onMouseDown={startResize} title="Resize"></div>

        <div className="image-header">
          <div className="image-title">ðŸ–¼ï¸ Zolaid Image Computer</div>
          <button className="image-close" onClick={onClose} type="button">
            âœ•
          </button>
        </div>

        <div className="image-permissions">
          <label className="image-toggle">
            <input type="checkbox" checked={approvalMode} onChange={(e) => setApprovalMode(e.target.checked)} />
            Require approval
          </label>
          <div className="image-chips">
            {["uploadImage", "process", "aiEdit", "download"].map((t) => (
              <button
                key={t}
                type="button"
                className={`image-chip ${allowedTypes.includes(t) ? "on" : "off"}`}
                onClick={() => setAllowedTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]))}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {awaitingApproval && currentAction && (
          <div className="image-approval">
            <div>
              <div className="image-approval-title">Approval required</div>
              <div className="image-approval-desc">{currentAction.description || currentAction.type}</div>
            </div>
            <div className="image-approval-actions">
              <button className="image-btn" type="button" onClick={approveNext}>
                Approve
              </button>
              <button className="image-btn" type="button" onClick={skipNext}>
                Skip
              </button>
              <button className="image-btn" type="button" onClick={reset}>
                Reset
              </button>
            </div>
          </div>
        )}

        <div className="image-body">
          <div className="image-panel">
            <div className="image-panel-title">Inputs</div>
            <label className="image-label">Image</label>
            <label className="image-toggle" style={{ marginBottom: 6 }}>
              <input
                type="checkbox"
                checked={batchMode}
                onChange={(e) => {
                  const on = e.target.checked;
                  setBatchMode(on);
                  setBatchFiles([]);
                  setBatchProgress(null);
                  applyToast(on ? "Batch mode on" : "Batch mode off");
                }}
              />
              Batch mode (multiple images)
            </label>
            <input
              type="file"
              accept="image/*"
              multiple={batchMode}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                const f = files[0] || null;
                setBatchFiles(batchMode ? files : []);
                setImageFile(f);
                setMaskFile(null);
                setOverlayFile(null);
                setOutputUrl(null);
                setVerification(null);
                setVersions([]);
                setCompareId(null);
                if (f) setImageUrl(URL.createObjectURL(f));
              }}
            />

            <div style={{ display: "flex", gap: "8px", marginTop: "10px", flexWrap: "wrap" }}>
              <button className={`image-btn ${mode === "local" ? "primary" : ""}`} type="button" onClick={() => setMode("local")}>
                Local
              </button>
              <button className={`image-btn ${mode === "ai" ? "primary" : ""}`} type="button" onClick={() => setMode("ai")}>
                AI (DALLâ€‘E)
              </button>
              <button className={`image-btn ${mode === "ai-plan" ? "primary" : ""}`} type="button" onClick={() => setMode("ai-plan")}>
                AI Plan
              </button>
            </div>

            {mode === "ai-plan" && (
              <>
                <label className="image-label" style={{ marginTop: "10px" }}>
                  Goal
                </label>
                <textarea value={aiGoal} onChange={(e) => setAiGoal(e.target.value)} rows={3} />
                {aiPlanContract?.artifacts?.plan && (
                  <div style={{ marginTop: 10 }}>
                    <div className="image-panel-title">AI Plan</div>
                    <div style={{ fontSize: 12, color: "rgba(0,0,0,0.75)", whiteSpace: "pre-wrap" }}>{String(aiPlanContract.artifacts.plan)}</div>
                  </div>
                )}
                {(aiPlanContract?.verification?.expected?.length || aiPlanContract?.verification?.checks?.length) && (
                  <div style={{ marginTop: 10 }}>
                    <div className="image-panel-title">Verification</div>
                    {Array.isArray(aiPlanContract?.verification?.expected) && aiPlanContract.verification.expected.length > 0 && (
                      <div style={{ fontSize: 12, color: "rgba(0,0,0,0.75)" }}>
                        <strong>Expected:</strong> {aiPlanContract.verification.expected.join(", ")}
                      </div>
                    )}
                    {Array.isArray(aiPlanContract?.verification?.checks) && aiPlanContract.verification.checks.length > 0 && (
                      <div style={{ fontSize: 12, color: "rgba(0,0,0,0.75)" }}>
                        <strong>Checks:</strong> {aiPlanContract.verification.checks.join(", ")}
                      </div>
                    )}
                  </div>
                )}
                <button className="image-btn primary" type="button" onClick={requestAiPlan} disabled={!aiGoal.trim()}>
                  Ask AI for plan
                </button>
                {aiPendingRecipe && (
                  <div style={{ marginTop: "10px" }}>
                    <div className="image-panel-title">AI Proposed Recipe</div>
                    <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, margin: 0, padding: 10, borderRadius: 12, background: "rgba(0,0,0,0.03)", border: "1px solid rgba(0,0,0,0.08)" }}>
                      {JSON.stringify(aiPendingRecipe, null, 2)}
                    </pre>
                    <div style={{ display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
                      <button
                        className="image-btn"
                        type="button"
                        onClick={() => {
                          applyRecipeToUi(aiPendingRecipe);
                          addCheckpoint("AI recipe applied");
                          applyToast("Recipe applied to controls");
                        }}
                      >
                        Apply to controls
                      </button>
                      <button
                        className="image-btn primary"
                        type="button"
                        disabled={!imageFile}
                        onClick={() => {
                          // Human approval gate: apply + run local process
                          applyRecipeToUi(aiPendingRecipe);
                          addCheckpoint("AI approved");
                          doLocalProcess();
                        }}
                      >
                        Approve + Run
                      </button>
                      <button className="image-btn" type="button" onClick={() => setAiPendingRecipe(null)}>
                        Reject
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {mode === "ai" && (
              <>
                <div style={{ display: "flex", gap: "8px", marginTop: "10px", flexWrap: "wrap" }}>
                  <button className={`image-btn ${!aiInpaint ? "primary" : ""}`} type="button" onClick={() => setAiInpaint(false)}>
                    AI Edit
                  </button>
                  <button className={`image-btn ${aiInpaint ? "primary" : ""}`} type="button" onClick={() => setAiInpaint(true)}>
                    Inpaint (masked)
                  </button>
                </div>
                <label className="image-label" style={{ marginTop: "10px" }}>
                  Prompt
                </label>
                <textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={3} />
                <label className="image-label">{aiInpaint ? "Mask (required for inpaint)" : "Mask (optional)"}</label>
                <input type="file" accept="image/*" onChange={(e) => setMaskFile(e.target.files?.[0] || null)} />
                <button className="image-btn primary" type="button" disabled={!imageFile || (aiInpaint ? !maskFile : !aiPrompt.trim())} onClick={doAiEdit}>
                  {aiInpaint ? "Run inpaint" : "Run AI edit"}
                </button>
              </>
            )}

            {mode === "local" && (
              <>
                <label className="image-label" style={{ marginTop: "10px" }}>
                  Mask for background removal (optional)
                </label>
                <input type="file" accept="image/*" onChange={(e) => setMaskFile(e.target.files?.[0] || null)} />

                <label className="image-label" style={{ marginTop: "10px" }}>
                  Overlay image (optional)
                </label>
                <input type="file" accept="image/*" onChange={(e) => setOverlayFile(e.target.files?.[0] || null)} />

                <div className="image-grid" style={{ marginTop: 10 }}>
                  <label className="image-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input type="checkbox" checked={removeBg} onChange={(e) => setRemoveBg(e.target.checked)} disabled={!maskFile} />
                    Remove background (mask)
                  </label>
                  <label className="image-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input type="checkbox" checked={removeBgInvert} onChange={(e) => setRemoveBgInvert(e.target.checked)} disabled={!removeBg || !maskFile} />
                    Invert mask
                  </label>
                  <label className="image-label">
                    Feather
                    <input value={removeBgFeather} onChange={(e) => setRemoveBgFeather(e.target.value)} disabled={!removeBg || !maskFile} />
                  </label>
                </div>
                {!maskFile && (
                  <div style={{ fontSize: 12, color: "rgba(0,0,0,0.65)", marginTop: 6 }}>
                    Upload a mask first (white = keep, black = remove).
                  </div>
                )}

                <div style={{ marginTop: 10 }}>
                  <div className="image-panel-title">Layers</div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {layerOrder.map((key, idx) => {
                      const label = key === "text" ? "Text overlay" : "Image overlay";
                      const exists = key === "text" ? Boolean(text.trim()) : Boolean(overlayFile);
                      const visible = key === "text" ? layerVisible.text !== false : layerVisible.image !== false;
                      return (
                        <div key={key} style={{ display: "flex", gap: 8, alignItems: "center", padding: 8, borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", opacity: exists ? 1 : 0.55 }}>
                          <button
                            className="image-btn"
                            type="button"
                            onClick={() =>
                              setLayerVisible((prev) => (key === "text" ? { ...prev, text: !visible } : { ...prev, image: !visible }))
                            }
                            disabled={!exists}
                            title="Toggle visibility"
                          >
                            {visible ? "Hide" : "Show"}
                          </button>
                          <div style={{ fontWeight: 800, fontSize: 12, flex: 1 }}>{label}</div>
                          <button
                            className="image-btn"
                            type="button"
                            disabled={idx === 0}
                            onClick={() => {
                              setLayerOrder((prev) => {
                                const next = [...prev];
                                const t = next[idx - 1];
                                next[idx - 1] = next[idx];
                                next[idx] = t;
                                return next;
                              });
                            }}
                          >
                            Up
                          </button>
                          <button
                            className="image-btn"
                            type="button"
                            disabled={idx === layerOrder.length - 1}
                            onClick={() => {
                              setLayerOrder((prev) => {
                                const next = [...prev];
                                const t = next[idx + 1];
                                next[idx + 1] = next[idx];
                                next[idx] = t;
                                return next;
                              });
                            }}
                          >
                            Down
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="image-grid">
                  <label className="image-label">
                    Preset
                    <select value={preset} onChange={(e) => setPreset(e.target.value)}>
                      <option value="none">None</option>
                      <option value="sepia">Sepia</option>
                      <option value="cinematic">Cinematic</option>
                    </select>
                  </label>
                  <label className="image-label">
                    Format
                    <select value={format} onChange={(e) => setFormat(e.target.value)}>
                      <option value="png">PNG</option>
                      <option value="jpeg">JPG</option>
                      <option value="webp">WEBP</option>
                    </select>
                  </label>
                  <label className="image-label">
                    Resize W
                    <input value={resizeW} onChange={(e) => setResizeW(e.target.value)} placeholder="e.g. 1024" />
                  </label>
                  <label className="image-label">
                    Resize H
                    <input value={resizeH} onChange={(e) => setResizeH(e.target.value)} placeholder="e.g. 1024" />
                  </label>
                  <label className="image-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input type="checkbox" checked={cropEnabled} onChange={(e) => setCropEnabled(e.target.checked)} />
                    Crop
                  </label>
                  <label className="image-label">
                    Rotate
                    <input value={rotate} onChange={(e) => setRotate(e.target.value)} />
                  </label>
                  <label className="image-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input type="checkbox" checked={flip} onChange={(e) => setFlip(e.target.checked)} />
                    Flip (vertical)
                  </label>
                  <label className="image-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input type="checkbox" checked={flop} onChange={(e) => setFlop(e.target.checked)} />
                    Flop (horizontal)
                  </label>
                  <label className="image-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input type="checkbox" checked={grayscale} onChange={(e) => setGrayscale(e.target.checked)} />
                    Grayscale
                  </label>
                  <label className="image-label">
                    Blur
                    <input value={blur} onChange={(e) => setBlur(e.target.value)} />
                  </label>
                  <label className="image-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input type="checkbox" checked={sharpen} onChange={(e) => setSharpen(e.target.checked)} />
                    Sharpen
                  </label>
                  <label className="image-label">
                    Brightness
                    <input value={brightness} onChange={(e) => setBrightness(e.target.value)} />
                  </label>
                  <label className="image-label">
                    Saturation
                    <input value={saturation} onChange={(e) => setSaturation(e.target.value)} />
                  </label>
                </div>

                {cropEnabled && (
                  <div className="image-grid" style={{ marginTop: 10 }}>
                    <label className="image-label">
                      Crop left
                      <input value={cropLeft} onChange={(e) => setCropLeft(e.target.value)} />
                    </label>
                    <label className="image-label">
                      Crop top
                      <input value={cropTop} onChange={(e) => setCropTop(e.target.value)} />
                    </label>
                    <label className="image-label">
                      Crop width
                      <input value={cropWidth} onChange={(e) => setCropWidth(e.target.value)} />
                    </label>
                    <label className="image-label">
                      Crop height
                      <input value={cropHeight} onChange={(e) => setCropHeight(e.target.value)} />
                    </label>
                  </div>
                )}

                <div className="image-grid" style={{ marginTop: 10 }}>
                  <label className="image-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input type="checkbox" checked={vignette} onChange={(e) => setVignette(e.target.checked)} />
                    Vignette
                  </label>
                  <label className="image-label">
                    Vignette strength
                    <input value={vignetteStrength} onChange={(e) => setVignetteStrength(e.target.value)} />
                  </label>
                  <label className="image-label">
                    Rounded corners
                    <input value={rounded} onChange={(e) => setRounded(e.target.value)} />
                  </label>
                  <label className="image-label">
                    Border size
                    <input value={borderSize} onChange={(e) => setBorderSize(e.target.value)} />
                  </label>
                  <label className="image-label">
                    Border color
                    <input value={borderColor} onChange={(e) => setBorderColor(e.target.value)} />
                  </label>
                </div>

                <div className="image-grid" style={{ marginTop: 10 }}>
                  <label className="image-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input type="checkbox" checked={shadow} onChange={(e) => setShadow(e.target.checked)} />
                    Drop shadow
                  </label>
                  <label className="image-label">
                    Shadow offset
                    <input value={shadowOffset} onChange={(e) => setShadowOffset(e.target.value)} />
                  </label>
                  <label className="image-label">
                    Shadow blur
                    <input value={shadowBlur} onChange={(e) => setShadowBlur(e.target.value)} />
                  </label>
                  <label className="image-label">
                    Shadow alpha
                    <input value={shadowAlpha} onChange={(e) => setShadowAlpha(e.target.value)} />
                  </label>
                </div>

                <div style={{ marginTop: "10px" }}>
                  <div className="image-panel-title">Text overlay</div>
                  <label className="image-label">Text</label>
                  <input value={text} onChange={(e) => setText(e.target.value)} />
                  <div className="image-grid">
                    <label className="image-label">
                      X
                      <input
                        value={textX}
                        onChange={(e) => setTextX(e.target.value)}
                        onBlur={() => {
                          const w = inputMeta?.width || 0;
                          const guides = w ? [0, 20, w / 2, w - 20] : [0, 20, 512 / 2, 512 - 20];
                          setTextX((v) => snapToGuides(v, guides, 12));
                        }}
                      />
                    </label>
                    <label className="image-label">
                      Y
                      <input
                        value={textY}
                        onChange={(e) => setTextY(e.target.value)}
                        onBlur={() => {
                          const h = inputMeta?.height || 0;
                          const guides = h ? [0, 20, h / 2, h - 20] : [0, 20, 512 / 2, 512 - 20];
                          setTextY((v) => snapToGuides(v, guides, 12));
                        }}
                      />
                    </label>
                    <label className="image-label">
                      Size
                      <input value={textSize} onChange={(e) => setTextSize(e.target.value)} />
                    </label>
                    <label className="image-label">
                      Color
                      <input value={textColor} onChange={(e) => setTextColor(e.target.value)} />
                    </label>
                  </div>
                </div>

                <button className="image-btn primary" type="button" disabled={!imageFile} onClick={doLocalProcess}>
                  Apply local edits
                </button>
                {batchProgress && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "rgba(0,0,0,0.7)" }}>
                    Batch: {batchProgress.done}/{batchProgress.total}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="image-panel">
            <div className="image-panel-title">Preview</div>
            <div className="image-preview-grid">
              {imageUrl && (
                <div>
                  <div className="image-preview-label">Original</div>
                  {cropEnabled && interactiveCrop ? (
                    <div className="image-cropper-wrap">
                      <Cropper
                        image={imageUrl}
                        crop={cropPos}
                        zoom={cropZoom}
                        aspect={cropAspect}
                        onCropChange={setCropPos}
                        onZoomChange={setCropZoom}
                        onCropComplete={(_area, areaPx) => setCroppedAreaPixels(areaPx)}
                        showGrid={cropGuide === "thirds"}
                      />
                      {cropGuide !== "none" && cropGuide !== "thirds" && (
                        <div className="image-crop-guides" aria-hidden="true">
                          {cropGuide === "center" && (
                            <>
                              <div className="g g-v" style={{ left: "50%" }} />
                              <div className="g g-h" style={{ top: "50%" }} />
                            </>
                          )}
                          {cropGuide === "golden" && (
                            <>
                              <div className="g g-v" style={{ left: "38.2%" }} />
                              <div className="g g-v" style={{ left: "61.8%" }} />
                              <div className="g g-h" style={{ top: "38.2%" }} />
                              <div className="g g-h" style={{ top: "61.8%" }} />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <img src={imageUrl} alt="original" className="image-preview" />
                  )}
                  {cropEnabled && (
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "8px" }}>
                      <button className="image-btn" type="button" onClick={() => setInteractiveCrop((v) => !v)}>
                        {interactiveCrop ? "Show static" : "Interactive crop"}
                      </button>
                      {interactiveCrop && (
                        <>
                          <label className="image-label" style={{ marginTop: 0 }}>
                            Zoom
                            <input
                              type="range"
                              min="1"
                              max="3"
                              step="0.01"
                              value={cropZoom}
                              onChange={(e) => setCropZoom(parseFloat(e.target.value))}
                            />
                          </label>
                          <label className="image-label" style={{ marginTop: 0 }}>
                            Aspect
                            <select value={cropAspect} onChange={(e) => setCropAspect(parseFloat(e.target.value))}>
                              <option value={1 / 1}>1:1</option>
                              <option value={16 / 9}>16:9</option>
                              <option value={9 / 16}>9:16</option>
                              <option value={4 / 3}>4:3</option>
                              <option value={3 / 4}>3:4</option>
                            </select>
                          </label>
                          <label className="image-label" style={{ marginTop: 0 }}>
                            Grid
                            <select value={cropGuide} onChange={(e) => setCropGuide(e.target.value)}>
                              <option value="thirds">Rule of thirds</option>
                              <option value="golden">Golden ratio</option>
                              <option value="center">Center guides</option>
                              <option value="none">None</option>
                            </select>
                          </label>
                          <button
                            className="image-btn primary"
                            type="button"
                            onClick={() => {
                              if (!croppedAreaPixels) return;
                              setCropLeft(String(Math.round(croppedAreaPixels.x)));
                              setCropTop(String(Math.round(croppedAreaPixels.y)));
                              setCropWidth(String(Math.round(croppedAreaPixels.width)));
                              setCropHeight(String(Math.round(croppedAreaPixels.height)));
                              addCheckpoint("Crop set");
                              applyToast("Crop region set");
                            }}
                          >
                            Use this crop
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
              {(outputUrl || compare?.url) && (
                <div style={{ display: "grid", gridTemplateColumns: compare?.url ? "1fr 1fr" : "1fr", gap: "10px" }}>
                  {outputUrl && (
                    <div>
                      <div className="image-preview-label">Output</div>
                      <img src={outputUrl} alt="output" className="image-preview" />
                    </div>
                  )}
                  {compare?.url && (
                    <div>
                      <div className="image-preview-label">Compare: {compare.name}</div>
                      <img src={compare.url} alt="compare" className="image-preview" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <div className="image-panel-title">Log</div>
            <div className="image-log">
              {(executedActions.length ? executedActions : actions).map((a, idx) => (
                <div key={idx} className="image-log-item">
                  <div className="image-log-type">{a.type}</div>
                  <div className="image-log-desc">{a.description || ""}</div>
                </div>
              ))}
              {!actions.length && <div className="image-log-empty">No actions yet</div>}
            </div>
          </div>

          <div className="image-panel">
            <div className="image-panel-title">Verification / Versions</div>
            {verification ? (
              <div className="image-verify">
                <div>
                  <strong>Status:</strong> {verification.ok ? "âœ… OK" : "âŒ Issues"}
                </div>
                {verificationFix && (
                  <div>
                    <strong>Auto-fix:</strong> {verificationFix}
                  </div>
                )}
                {verification.width && (
                  <div>
                    <strong>Size:</strong> {verification.width}Ã—{verification.height}
                  </div>
                )}
              </div>
            ) : (
              <div className="image-log-empty">No verification report yet</div>
            )}

            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" }}>
              <button className="image-btn" type="button" onClick={() => addCheckpoint("Manual checkpoint")}>
                Checkpoint
              </button>
              <button className="image-btn" type="button" disabled={actionIndex + 1 >= actionSteps.length} onClick={undo}>
                Undo
              </button>
              <button className="image-btn" type="button" disabled={actionIndex <= 0} onClick={redo}>
                Redo
              </button>
              <button className="image-btn" type="button" onClick={saveProjectJson}>
                Save project JSON
              </button>
              <input
                ref={projectFileInputRef}
                type="file"
                accept="application/json,.json"
                style={{ display: "none" }}
                onChange={(e) => loadProjectJson(e.target.files?.[0] || null)}
              />
              <button className="image-btn" type="button" onClick={() => projectFileInputRef.current?.click()}>
                Load project JSON
              </button>
              <button
                className="image-btn"
                type="button"
                disabled={!outputUrl}
                onClick={() => {
                  if (!outputUrl) return;
                  const id = Date.now().toString();
                  const name = `Branch ${versions.length + 1}`;
                  setVersions((prev) => [{ id, name, url: outputUrl, recipe, verification }, ...prev].slice(0, 25));
                  applyToast("Saved version");
                }}
              >
                Save version
              </button>
              <button className="image-btn" type="button" disabled={!versions.length} onClick={() => setCompareId(versions[0]?.id || null)}>
                Compare latest
              </button>
              <button className="image-btn" type="button" disabled={!outputUrl} onClick={() => onActionComplete({ type: "download" })}>
                Download output
              </button>
              <button className="image-btn" type="button" disabled={!outputUrl} onClick={() => exportOutput("png")}>
                Export PNG
              </button>
              <button className="image-btn" type="button" disabled={!outputUrl} onClick={() => exportOutput("jpeg")}>
                Export JPG
              </button>
              <button className="image-btn" type="button" disabled={!outputUrl} onClick={() => exportOutput("webp")}>
                Export WEBP
              </button>
            </div>

            {versions.length > 0 && (
              <label className="image-label" style={{ marginTop: "10px" }}>
                Compare version
                <select value={compareId || ""} onChange={(e) => setCompareId(e.target.value || null)}>
                  <option value="">None</option>
                  {versions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {versions.length > 0 && (
              <div style={{ display: "flex", gap: "8px", overflowX: "auto", marginTop: "10px", paddingBottom: "6px" }}>
                {versions.slice(0, 12).map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    className="image-btn"
                    style={{ minWidth: 120, padding: 6, borderRadius: 12 }}
                    onClick={() => setCompareId(v.id)}
                    title={v.name}
                  >
                    <div style={{ fontSize: 11, fontWeight: 800, marginBottom: 6, textAlign: "left" }}>{v.name}</div>
                    <img
                      src={v.url}
                      alt={v.name}
                      loading="lazy"
                      style={{ width: 100, height: 70, objectFit: "cover", borderRadius: 10, border: "1px solid rgba(0,0,0,0.12)" }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


